import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { Button } from '../components/ui/button';
import { SEO } from '../components/SEO';
import { StructuredData } from '../components/StructuredData';

const PortfolioPage = () => {
  const navigate = useNavigate();
  const [isVisible, setIsVisible] = useState(false);

  // Scroll to top on component mount
  useEffect(() => {
    window.scrollTo(0, 0);
    // Trigger header animation
    const timer = setTimeout(() => setIsVisible(true), 300);
    return () => clearTimeout(timer);
  }, []);

  const structuredData = useMemo(() => ({
    "name": "Investment Portfolio - Sector and Market Cap Distribution",
    "description": "Savi Investing's concentrated equity portfolio breakdown showing sector allocation and market capitalization distribution across 6 stocks.",
    "url": "https://saviinvesting.in/portfolio"
  }), []);

  return (
    <div className="min-h-screen flex flex-col">
      <SEO 
        title="Our Portfolio | Savi Investing"
        description="Take a peek inside our portfolio of select Indian equities - sector-wise and market cap-wise."
      />
      <StructuredData 
        type="WebPage"
        data={structuredData}
      />
      <Header />
      
      <main className="flex-grow pt-24">
        {/* Page Header */}
        <section className="bg-[#29367A] text-white py-24">
          <div className="w-full max-w-7xl mx-auto px-8">
            <h1 
              className={`text-5xl font-bold text-center transition-all duration-1000 transform ${
                isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
              }`}
            >
              Inside Our Portfolio
            </h1>
          </div>
        </section>

        {/* Introduction */}
        <section className="py-10 bg-white">
          <div className="w-full max-w-7xl mx-auto px-8">
            <p className="text-lg text-gray-700 leading-relaxed text-center max-w-4xl mx-auto">
              The flagship, proprietary portfolio of Savi Investing is a concentrated accumulation of the equity stocks of a select few businesses that tick all, or most, of the boxes we consider important.
            </p>
          </div>
        </section>

        {/* Sector-wise Distribution */}
        <section className="py-10 bg-gray-50">
          <div className="w-full max-w-7xl mx-auto px-8">
            <h2 className="text-3xl font-bold text-gray-900 text-center mb-12">
              PORTFOLIO CONSTITUENTS - SECTOR-WISE
            </h2>
            
            {/* Pie Chart Image */}
            <div className="flex justify-center mb-8">
              <img
                src="https://customer-assets.emergentagent.com/job_disciplined-returns/artifacts/011l7zgj_1.png"
                alt="Sector-wise Portfolio Distribution"
                className="max-w-3xl w-full h-auto"
              />
            </div>

            {/* Footer */}
            <p className="text-center text-gray-600 italic">
              Data as of: August 2026, No. of Equity Stocks: 6
            </p>
          </div>
        </section>

        {/* Market Cap Distribution */}
        <section className="py-10 bg-white">
          <div className="w-full max-w-7xl mx-auto px-8">
            <h2 className="text-3xl font-bold text-gray-900 text-center mb-12">
              PORTFOLIO CONSTITUENTS - MARKET CAP-WISE
            </h2>
            
            {/* Pie Chart Image */}
            <div className="flex justify-center mb-8">
              <img
                src="/assets/market-cap-distribution.png"
                alt="Market Cap-wise Portfolio Distribution"
                className="max-w-3xl w-full h-auto"
              />
            </div>

            {/* Footer */}
            <p className="text-center text-gray-600 italic mb-8">
              Data as of: August 2026, No. of Equity Stocks: 6
            </p>

            {/* CTA Button */}
            <div className="flex justify-center">
              <Button
                onClick={() => navigate('/framework')}
                className="bg-[#29367A] hover:bg-[#1f2859] text-white px-8 py-6 text-base font-semibold rounded-lg transition-all duration-300 shadow-lg hover:shadow-xl"
              >
                Know How We Invest
              </Button>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default PortfolioPage;
